import { GlobalPlayerManager } from './globalPlayerManager';
import { playerErrorMonitor } from './playerErrorMonitor';
import { usePlayerStore } from '@/stores/playerStore';
import { logger } from './devUtils';

/**
 * 调试信息接口
 */
interface DebugInfo {
  playerInstance: {
    exists: boolean;
    state?: any;
  };
  storeState: {
    playbackState: string | undefined;
    currentTime: number;
    totalTime: number;
    quality: string;
    speed: string;
    cid: string | null;
    vid: string | null;
    videoTitle: string;
  };
  errors: {
    total: number;
    recent: any[];
    statistics: any;
  };
  system: {
    userAgent: string;
    url: string;
    screen: {
      width: number;
      height: number;
    };
    viewport: {
      width: number;
      height: number;
    };
    memory?: {
      usedJSHeapSize?: number;
      totalJSHeapSize?: number;
      jsHeapSizeLimit?: number;
    };
  };
}

/**
 * 播放器调试工具类
 * 提供全局调试接口，方便在控制台排查问题
 */
class PlayerDebug {
  /**
   * 获取完整的调试信息
   */
  static getDebugInfo(): DebugInfo {
    const manager = GlobalPlayerManager.getInstance();
    const playerInstance = manager.getPlayerInstance();
    const store = usePlayerStore.getState();

    // 收集系统信息
    const systemInfo: DebugInfo['system'] = {
      userAgent: navigator.userAgent,
      url: window.location.href,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };

    // 尝试获取内存信息（Chrome）
    if ('memory' in performance) {
      const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = (performance as any).memory;
      systemInfo.memory = {
        usedJSHeapSize,
        totalJSHeapSize,
        jsHeapSizeLimit,
      };
    }

    return {
      playerInstance: {
        exists: !!playerInstance,
        state: playerInstance?.currentVideoInfo?.state ?? playerInstance?.getState?.() ?? null,
      },
      storeState: {
        playbackState: store.getPlaybackState?.(),
        currentTime: store.currentTime,
        totalTime: store.totalTime,
        quality: store.quality,
        speed: store.speed,
        cid: store.cid,
        vid: store.vid,
        videoTitle: store.videoTitle,
      },
      errors: {
        total: playerErrorMonitor.getErrors().length,
        recent: playerErrorMonitor.getRecentErrors(5),
        statistics: playerErrorMonitor.getStatistics(),
      },
      system: systemInfo,
    };
  }

  /**
   * 导出调试信息为 JSON 字符串
   */
  static exportDebugInfo(): string {
    const info = PlayerDebug.getDebugInfo();
    return JSON.stringify(info, null, 2);
  }

  /**
   * 复制调试信息到剪贴板
   */
  static async copyDebugInfo(): Promise<void> {
    const info = PlayerDebug.exportDebugInfo();

    try {
      // 尝试使用现代剪贴板 API
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(info);
        logger.log('[player] ✅ 调试信息已复制到剪贴板');
        return;
      }

      // 降级方案：创建临时 textarea
      const textarea = document.createElement('textarea');
      textarea.value = info;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      logger.log('[player] ✅ 调试信息已复制到剪贴板（降级方案）');
    } catch (err) {
      logger.error('[player] ❌ 复制失败:', err);
      logger.log('[player] 调试信息:\n', info);
    }
  }

  /**
   * 打印播放器状态
   */
  static printPlayerState(): void {
    const manager = GlobalPlayerManager.getInstance();
    const playerInstance = manager.getPlayerInstance();

    if (!playerInstance) {
      logger.log('[player] ⚠️ 播放器实例不存在');
      return;
    }

    logger.log('[player] 播放器状态');
    logger.log('实例:', playerInstance);
    logger.log('状态:', playerInstance.getState?.());
    logger.log('容器:', document.getElementById('global-player-container'));
  }

  /**
   * 打印 Store 状态
   */
  static printStoreState(): void {
    const store = usePlayerStore.getState();

    logger.log('[player] Store 状态');
    logger.log('播放状态:', store.getPlaybackState?.());
    logger.log('当前时间:', store.currentTime);
    logger.log('总时长:', store.totalTime);
    logger.log('清晰度:', store.quality);
    logger.log('倍速:', store.speed);
    logger.log('CID:', store.cid);
    logger.log('VID:', store.vid);
    logger.log('标题:', store.videoTitle);
  }

  /**
   * 打印错误列表
   */
  static printErrors(): void {
    const errors = playerErrorMonitor.getErrors();
    const stats = playerErrorMonitor.getStatistics();

    logger.log('[player] 错误列表');
    logger.log('总计:', stats.total);
    logger.log('统计:', stats.byType);
    console.table(errors);
  }

  /**
   * 清除所有错误
   */
  static clearErrors(): void {
    playerErrorMonitor.clearErrors();
    logger.log('[player] ✅ 错误记录已清空');
  }

  /**
   * 下载调试信息为文件
   */
  static downloadDebugInfo(filename = 'player-debug.json'): void {
    const info = PlayerDebug.exportDebugInfo();
    const blob = new Blob([info], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
    logger.log('[player] ✅ 调试信息已下载:', filename);
  }

  /**
   * 测试播放器功能
   */
  static async testPlayer(): Promise<void> {
    logger.log('[player] 播放器功能测试');

    const manager = GlobalPlayerManager.getInstance();
    const playerInstance = manager.getPlayerInstance();

    if (!playerInstance) {
      logger.log('[player] ❌ 播放器实例不存在');
      return;
    }

    // 测试播放/暂停
    try {
      logger.log('[player] 测试播放/暂停...');
      const store = usePlayerStore.getState();

      if (store.getPlaybackState?.() === 'playing') {
        await store.pause();
        logger.log('[player] ✅ 暂停成功');
        await new Promise((r) => setTimeout(r, 1000));
        await store.play();
        logger.log('[player] ✅ 播放成功');
      } else {
        await store.play();
        logger.log('[player] ✅ 播放成功');
        await new Promise((r) => setTimeout(r, 1000));
        await store.pause();
        logger.log('[player] ✅ 暂停成功');
      }
    } catch (err) {
      logger.error('[player] ❌ 播放/暂停测试失败:', err);
    }
  }

  /**
   * 显示帮助信息
   */
  static help(): void {
    console.log(
      `
%c播放器调试工具 - 使用说明
%c================================

%c基本命令:
  playerDebug.getDebugInfo()      - 获取完整调试信息
  playerDebug.exportDebugInfo()   - 导出为 JSON 字符串
  playerDebug.copyDebugInfo()     - 复制到剪贴板
  playerDebug.downloadDebugInfo() - 下载为文件

%c状态查看:
  playerDebug.printPlayerState()  - 打印播放器状态
  playerDebug.printStoreState()   - 打印 Store 状态
  playerDebug.printErrors()       - 打印错误列表

%c错误管理:
  playerDebug.clearErrors()       - 清空错误记录

%c测试功能:
  playerDebug.testPlayer()        - 测试播放器功能

%c帮助:
  playerDebug.help()              - 显示此帮助信息
    `,
      'color: #4CAF50; font-size: 16px; font-weight: bold',
      'color: #4CAF50',
      'color: #2196F3; font-weight: bold',
      'color: #FF9800; font-weight: bold',
      'color: #F44336; font-weight: bold',
      'color: #9C27B0; font-weight: bold',
      'color: #607D8B; font-weight: bold'
    );
  }
}

// 暴露到全局，方便在控制台调用
if (typeof window !== 'undefined') {
  (window as any).playerDebug = PlayerDebug;
  console.log('%c[PlayerDebug] 调试工具已就绪，输入 playerDebug.help() 查看帮助', 'color: #4CAF50; font-weight: bold');
}

export { PlayerDebug };
